<template>
  <AuthLayout
    ><AuthCard
      ><template #title>{{ title }}</template
      ><template #intro>{{ intro }}</template>
      <div v-if="checking" role="status" aria-live="polite">
        Checking your account…
      </div>
      <section v-else-if="terminal" role="status" aria-live="polite">
        <p>{{ status }}</p>
        <router-link class="button" :to="terminalCta.to">{{
          terminalCta.label
        }}</router-link
        ><button
          v-if="canResend"
          class="secondary"
          type="button"
          @click="resend"
        >
          Send another verification email
        </button>
      </section>
      <template v-else-if="mode === 'verify'"
        ><div role="status" aria-live="polite">{{ status }}</div></template
      >
      <template v-else-if="mode === 'forgotSent'"
        ><p role="status">
          If an account matches that address, reset instructions are on the way.
        </p>
        <router-link class="button" to="/account/login"
          >Return to sign in</router-link
        ></template
      >
      <template v-else-if="mode === 'verifySent'"
        ><p role="status">
          Your account is ready. Check your inbox for the verification link; you
          can continue using your account now.
        </p>
        <router-link class="button" to="/account"
          >Continue to your account</router-link
        ><button
          v-if="canResend"
          class="secondary"
          type="button"
          @click="resend"
        >
          Send another email
        </button>
        <p v-if="status" role="status" aria-live="polite">
          {{ status }}
        </p></template
      >
      <form v-else novalidate @submit.prevent="submit">
        <p
          v-if="mode === 'login' && route.query.reason === 'expired'"
          class="session-notice"
          role="status"
        >
          Your session ended. Sign in again to continue safely.
        </p>
        <FormErrorSummary
          :message="errorMessage"
          :items="Object.values(errors)"
        />
        <div
          v-if="mode === 'register' && policyState !== 'ready'"
          role="status"
          aria-live="polite"
        >
          <p>
            {{
              policyState === "error"
                ? "We couldn’t load the current Terms. Registration is unavailable until this succeeds."
                : "Loading the current Terms…"
            }}
          </p>
          <button
            v-if="policyState === 'error'"
            class="secondary"
            type="button"
            @click="loadPolicy"
          >
            Retry loading Terms
          </button>
        </div>
        <div v-if="mode === 'register'" class="row">
          <AuthField
            id="first-name"
            v-model="form.firstName"
            label="First name"
            autocomplete="given-name"
            :error="errors.firstName"
          /><AuthField
            id="last-name"
            v-model="form.lastName"
            label="Last name"
            autocomplete="family-name"
            :error="errors.lastName"
          />
        </div>
        <AuthField
          v-if="mode !== 'reset'"
          id="email"
          v-model="form.email"
          label="Email address"
          type="email"
          autocomplete="email"
          :error="errors.email"
        />
        <AuthField
          v-if="mode === 'register'"
          id="phone"
          v-model="form.phone"
          label="Phone (optional)"
          type="tel"
          autocomplete="tel"
          :error="errors.phone"
        />
        <PasswordField
          v-if="mode === 'login'"
          id="password"
          v-model="form.password"
          name="password"
          label="Password"
          autocomplete="current-password"
          :error="errors.password"
        />
        <PasswordField
          v-if="mode === 'register' || mode === 'reset'"
          id="password"
          v-model="form.password"
          name="password"
          label="New password"
          autocomplete="new-password"
          :error="errors.password"
        />
        <PasswordField
          v-if="mode === 'register' || mode === 'reset'"
          id="confirm-password"
          v-model="form.confirmPassword"
          name="confirm-password"
          label="Confirm password"
          autocomplete="new-password"
          :error="errors.passwordConfirmation || errors.confirmPassword"
        />
        <div v-if="mode === 'register'">
          <label class="check"
            ><input
              v-model="form.termsAccepted"
              type="checkbox"
              :aria-invalid="!!errors.termsAccepted"
              :aria-describedby="
                errors.termsAccepted ? 'terms-error' : undefined
              "
            />
            <span
              >I accept the
              <a :href="policy.termsUrl" target="_blank" rel="noopener"
                >Terms</a
              ></span
            ></label
          >
          <p v-if="errors.termsAccepted" id="terms-error" class="error">
            {{ errors.termsAccepted }}
          </p>
        </div>
        <label v-if="mode === 'register'" class="check"
          ><input v-model="form.newsletter" type="checkbox" /> Send me product
          news (optional)</label
        >
        <button
          class="button"
          :disabled="
            submitting || (mode === 'register' && policyState !== 'ready')
          "
        >
          {{ submitting ? "Please wait…" : button }}
        </button>
        <p class="links">
          <router-link v-if="mode === 'login'" to="/account/forgot-password"
            >Forgot password?</router-link
          ><router-link v-if="mode === 'login'" to="/account/register"
            >Create an account</router-link
          ><router-link v-else to="/account/login"
            >Return to sign in</router-link
          >
        </p>
        <p class="sr-status" aria-live="polite">{{ status }}</p>
      </form></AuthCard
    ></AuthLayout
  >
</template>
<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { useMeta } from "vue-meta";
import AuthLayout from "./AuthLayout.vue";
import AuthCard from "./AuthCard.vue";
import AuthField from "./AuthField.vue";
import FormErrorSummary from "./FormErrorSummary.vue";
import PasswordField from "./PasswordField.vue";
import {
  fieldErrors,
  passwordByteLength,
  safeReturnTo,
} from "~/core/account/auth";
const route = useRoute(),
  router = useRouter(),
  store = useStore();
useMeta({
  meta: [{ name: "robots", content: "noindex, follow, noarchive" }],
});
const mode = computed(() =>
  route.path.endsWith("/register")
    ? "register"
    : route.path.endsWith("/forgot-password/sent")
    ? "forgotSent"
    : route.path.endsWith("/forgot-password")
    ? "forgot"
    : route.path.endsWith("/reset-password")
    ? "reset"
    : route.path.endsWith("/verify-email/sent")
    ? "verifySent"
    : route.path.endsWith("/verify-email")
    ? "verify"
    : "login"
);
const form = reactive({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
  confirmPassword: "",
  termsAccepted: false,
  newsletter: false,
});
const submitting = ref(false),
  status = ref(""),
  errorMessage = ref(""),
  errors = ref<Record<string, string>>({}),
  terminal = ref(false),
  terminalCode = ref(""),
  resetToken = ref("");
const checking = computed(() => store.getters["common/customer/checking"]);
const policyState = computed(
  () => store.getters["common/customer/registrationPolicyState"] || "idle"
);
const policy = computed(
  () =>
    store.getters["common/customer/registrationPolicy"] || {
      termsVersion: "",
      termsUrl: "/terms",
    }
);
const canResend = computed(
  () =>
    store.getters["common/customer/auth"] &&
    store.getters["common/customer/verificationRequired"]
);
const title = computed(
  () =>
    ({
      login: "Sign in",
      register: "Create your account",
      forgot: "Reset your password",
      forgotSent: "Check your email",
      reset: "Choose a new password",
      verify: "Verify your email",
      verifySent: "Check your email",
    }[mode.value])
);
const intro = computed(
  () =>
    ({
      login: "Welcome back.",
      register: "Create an account to manage your eyewear journey.",
      forgot:
        "Enter your email. We’ll send instructions if an account matches.",
      forgotSent: "For privacy, this confirmation is always the same.",
      reset: "Use between 10 and 72 bytes.",
      verify: "We’re checking your secure link.",
      verifySent: "Verification is helpful but does not block your account.",
    }[mode.value])
);
const button = computed(
  () =>
    ({
      login: "Sign in",
      register: "Create account",
      forgot: "Send reset instructions",
      reset: "Save new password",
    }[mode.value])
);
const terminalCta = computed(() =>
  terminalCode.value === "VERIFICATION_SUCCESS"
    ? store.getters["common/customer/auth"]
      ? { to: "/account", label: "Continue to account" }
      : { to: "/account/login", label: "Sign in" }
    : terminalCode.value.startsWith("VERIFICATION")
    ? { to: "/account/login", label: "Sign in to resend" }
    : terminalCode.value.startsWith("RESET_TOKEN")
    ? { to: "/account/forgot-password", label: "Request a new reset link" }
    : { to: "/account/login", label: "Sign in" }
);
function validate() {
  errors.value = {};
  if (mode.value !== "reset" && !/^\S+@\S+\.\S+$/.test(form.email))
    errors.value.email = "Enter a valid email address.";
  if (mode.value === "register") {
    if (!form.firstName.trim())
      errors.value.firstName = "Enter your first name.";
    if (!form.lastName.trim()) errors.value.lastName = "Enter your last name.";
    if (!form.termsAccepted)
      errors.value.termsAccepted = "Accept the Terms to continue.";
    if (policyState.value !== "ready" || !policy.value.termsVersion)
      errors.value.termsAccepted =
        "Load the current Terms successfully before registering.";
  }
  if (
    ["login", "register", "reset"].includes(mode.value) &&
    form.password.length < (mode.value === "login" ? 1 : 10)
  )
    errors.value.password =
      mode.value === "login"
        ? "Enter your password."
        : "Use at least 10 characters.";
  if (
    ["register", "reset"].includes(mode.value) &&
    passwordByteLength(form.password) > 72
  )
    errors.value.password = "Use no more than 72 bytes.";
  if (
    ["register", "reset"].includes(mode.value) &&
    form.password !== form.confirmPassword
  )
    errors.value.passwordConfirmation = "Passwords must match.";
  errorMessage.value = Object.keys(errors.value).length
    ? "Please correct the highlighted fields."
    : "";
  return !errorMessage.value;
}
async function submit() {
  if (submitting.value || !validate()) return;
  submitting.value = true;
  status.value = "";
  let result: any;
  try {
    if (mode.value === "login")
      result = await store.dispatch("common/customer/login", {
        email: form.email,
        password: form.password,
      });
    if (mode.value === "register")
      result = await store.dispatch("common/customer/register", {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone || null,
        password: form.password,
        passwordConfirmation: form.confirmPassword,
        termsVersion: policy.value.termsVersion,
        termsAccepted: form.termsAccepted,
        newsletter: form.newsletter,
      });
    if (mode.value === "forgot")
      result = await store.dispatch(
        "common/customer/requestPasswordReset",
        form.email
      );
    if (mode.value === "reset")
      result = await store.dispatch("common/customer/resetPassword", {
        token: resetToken.value,
        password: form.password,
        passwordConfirmation: form.confirmPassword,
      });
  } catch {
    result = {
      ok: false,
      error: {
        message: "We couldn’t reach the account service.",
        fieldErrors: [],
      },
    };
  } finally {
    submitting.value = false;
  }
  if (result?.ok) {
    if (mode.value === "login")
      return router.push(safeReturnTo(route.query.returnTo));
    if (mode.value === "register")
      return router.push("/account/verify-email/sent");
    if (mode.value === "forgot")
      return router.push("/account/forgot-password/sent");
    if (mode.value === "reset") {
      terminal.value = true;
      status.value = "Your password has been changed. You can now sign in.";
    }
  } else {
    errors.value = fieldErrors(result?.error || null);
    errorMessage.value = result?.error?.message || "Please try again.";
    terminalCode.value = result?.error?.code || "";
    if (
      mode.value === "reset" &&
      ["RESET_TOKEN_INVALID", "RESET_TOKEN_EXPIRED"].includes(
        terminalCode.value
      )
    ) {
      terminal.value = true;
      status.value = errorMessage.value;
    }
  }
}
async function resend() {
  if (submitting.value) return;
  submitting.value = true;
  try {
    const result = await store.dispatch("common/customer/resendVerification");
    status.value = result?.ok
      ? "Another verification email is on the way."
      : result?.error?.message || "Please try again.";
  } finally {
    submitting.value = false;
  }
}
async function loadPolicy() {
  errorMessage.value = "";
  try {
    await store.dispatch("common/customer/loadRegistrationPolicy");
  } catch {
    errorMessage.value = "Registration is temporarily unavailable.";
  }
}
async function initialize() {
  terminal.value = false;
  status.value = "";
  if (store.getters["common/customer/authState"] === "checking")
    await store.dispatch("common/customer/checkLogged");
  if (mode.value === "register" && policyState.value !== "ready")
    await loadPolicy();
  if (mode.value === "reset") {
    resetToken.value = String(route.query.token || "");
    if (route.query.token && typeof window !== "undefined")
      window.history.replaceState(window.history.state, "", route.path);
  }
  if (mode.value === "verify") {
    const token = String(route.query.token || "");
    if (route.query.token && typeof window !== "undefined")
      window.history.replaceState(window.history.state, "", route.path);
    const result = await store.dispatch("common/customer/verifyEmail", token);
    terminal.value = true;
    terminalCode.value = result?.ok
      ? "VERIFICATION_SUCCESS"
      : result?.error?.code || "";
    status.value = result?.ok
      ? "Your email is verified."
      : result?.error?.message ||
        "This verification link is invalid, expired, or already used.";
  } else if (
    ["login", "register"].includes(mode.value) &&
    store.getters["common/customer/auth"]
  )
    router.replace(safeReturnTo(route.query.returnTo));
}
onMounted(initialize);
watch(() => route.path, initialize);
</script>
<style scoped>
.check input:focus-visible,
.button:focus-visible,
.secondary:focus-visible,
.links a:focus-visible {
  outline: 3px solid #ee7203;
  outline-offset: 2px;
}
.error {
  color: #9d1c13;
  margin: 0.35rem 0 0;
}
.session-notice {
  margin: 0 0 1rem;
  padding: 0.8rem 1rem;
  border-left: 4px solid #ee7203;
  background: #fff7ed;
  color: #071c39;
  font-weight: 650;
}
.row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.check {
  display: flex;
  gap: 0.65rem;
  align-items: flex-start;
  margin: 1rem 0;
  line-height: 1.4;
}
.check input {
  width: 22px;
  height: 22px;
  flex: none;
}
.button,
.secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  min-height: 48px;
  margin-top: 0.75rem;
  padding: 0.75rem 1rem;
  border: 0;
  border-radius: 0.3rem;
  background: #071c39;
  color: #fff;
  font-weight: 700;
  text-decoration: none;
}
.secondary {
  background: #fff;
  color: #071c39;
  border: 2px solid #071c39;
}
.button:disabled {
  opacity: 0.6;
}
.links {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin: 1rem 0 0;
}
.links a {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  color: #071c39;
}
.sr-status {
  min-height: 1.5rem;
}
@media (max-width: 520px) {
  .row {
    grid-template-columns: 1fr;
  }
  .links {
    display: grid;
  }
}
</style>
